import React, { ButtonHTMLAttributes, useMemo } from 'react';
import { IconType } from '@app/icons';
import Loader from '../Loader';
import { DefaultTFuncReturn } from 'i18next';
import { Button as ShadcnButton } from '../ui/button';
import { cn } from 'lib/utils';

type ButtonColors = 'default' | 'primary' | 'danger' | 'cyan';

interface ButtonProps
  extends Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'onClick' | 'className'
  > {
  text?: string | DefaultTFuncReturn;
  variant?: 'contained' | 'outlined' | 'text';
  icon?: IconType;
  disabled?: boolean;
  color?: ButtonColors;
  loading?: boolean;
}

function Button(props: ButtonProps) {
  const {
    type = 'button',
    onClick,
    text,
    className = '',
    variant = 'contained',
    icon: Icon,
    disabled = false,
    color = 'primary',
    loading = false,
  } = props;

  const buttonVariant = useMemo(() => {
    switch (variant) {
      case 'contained':
        return 'default';
      case 'outlined':
        return 'outline';
      case 'text':
        return 'ghost';
      default:
        return 'default';
    }
  }, [variant]);

  const buttonColorClassName = useMemo<string>(() => {
    switch (color) {
      case 'default':
        return 'bg-gray-500 hover:bg-gray-600';
      case 'primary':
        return 'bg-blue-600 hover:bg-blue-700';
      case 'danger':
        return 'bg-red-600 hover:bg-red-700';
      case 'cyan':
        return 'bg-cyan-600 hover:bg-cyan-700';
      default:
        return '';
    }
  }, [color]);

  return (
    <ShadcnButton
      className={cn(
        buttonColorClassName,
        {
          'opacity-50 cursor-not-allowed': disabled || loading,
        },
        className
      )}
      variant={buttonVariant as any}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && (
        <div className="mr-2">
          <Loader size="small" color="default" />
        </div>
      )}
      {!!Icon && (
        <span className="mr-2">
          <Icon />
        </span>
      )}
      {!!text && <span>{text}</span>}
    </ShadcnButton>
  );
}

export default Button;
